'use client';

// npm packages
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

// components
import { cn } from '@/utils/cn';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import {
  MESSENGER_BASE_URL,
  SWIPE,
  WHATSAPP_BASE_URL,
  WHATSAPP_BASE_URL_MOBILE,
} from '@/constants';
import useWindowDimensions from '@/app/hooks/useWindowSize';
import Text from '../Text';
import Icon from '../Icon';
import Button from '../Button';

function ChatMenuModal({ onView, onClose, title }) {
  const modalRef = useRef(null);
  const windowDimensions = useWindowDimensions();
  const [isMobile, setIsMobile] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const { usernames } = useSelector((state) => state.chatLink);
  const buttonTextStyles =
    'text-coarse-wool !font-medium !text-[18px] !leading-[28px] !tracking-[0.02em]';

  /* gesture close for mobile */
  const [translateY, setTranslateY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    setOpenModal(onView);

    // Check if the window is small on mount
    const checkMobileView = () => {
      setIsMobile(windowDimensions?.width < 1024);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, [onView]);

  /* gesture close for mobile */
  const handleTranslateY = (value) => {
    setTranslateY(value);
  };

  const handleClose = (state) => {
    if (state === SWIPE) {
      setIsClosing(true);
      onClose(false);
      setTimeout(() => {
        setTranslateY(0);
        setIsClosing(false);
      }, 200);
    } else {
      onClose(false);
    }
  };

  if (!openModal) return null;

  return (
    <>
      {/* Blurred Background */}
      {isMobile ? (
        <div
          role="presentation"
          className={classNames(
            'fixed bg-black/30 backdrop-blur-sm transition-opacity duration-300',
            { block: openModal, hidden: !openModal },
          )}
          onClick={handleClose}
        />
      ) : null}

      <div
        ref={modalRef}
        style={{
          transform: `translateY(${translateY}px)`,
          transition: isClosing
            ? 'transform 0.3s ease-out, opacity 0.3s ease-out'
            : 'transform 0.3s ease-out',
        }}
        className={classNames(
          'bg-white rounded-t-xl sm:rounded-xl h-full lg:w-full p-[20px] transition-transform duration-300 ease-out relative shadow-md',
          {
            // Desktop modal
            'translate-y-0 block z-[198] fixed': openModal && !isMobile,
            '-translate-y-2 hidden z-0 hidden': !openModal && !isMobile,

            // Mobile modal
            'fixed !bottom-0 translate-y-0 block w-full': openModal && isMobile,
            'translate-y-full z-0 hidden': !openModal && isMobile,
          },
        )}
      >
        {/* gesture close for mobile */}
        <Button
          type="modal-swipe-close-button"
          onTranslateY={handleTranslateY}
          onClose={handleClose}
        />

        <div className="flex justify-between items-center sm:mb-[20px] mb-[16px]">
          <Text
            type="section_title"
            className="text-coarse-wool font-semibold !text-[24px] leading-[28px] tracking-[0.02em]"
          >
            {title}
          </Text>
          <Icon
            type="close"
            color="#49525E"
            className="cursor-pointer min-w-[24px]"
            onClick={handleClose}
          />
        </div>

        <div className="flex flex-col justify-start items-between gap-[20px] pb-[24px]">
          <div className="h-[40px]">
            <Text
              type="section_sub_title"
              className="text-coarse-wool !font-medium !text-[16px] !leading-[20px] !tracking-[2%]"
            >
              Get faster answers! Connect with us for personalized support and
              expert advice.
            </Text>
          </div>

          {/* whatsapp icon  */}
          <div className="flex flex-row justify-start items-center w-[320px] h-[48px] lg:w-[372px] lg:h-[52px] p-[8px] lg:p-[8px]">
            <Link
              prefetch={false}
              aria-label="Go to whatsapp chat"
              href={`${isMobile ? WHATSAPP_BASE_URL_MOBILE : WHATSAPP_BASE_URL}${usernames.whatsapp}`}
              passHref
              type="link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClose}
              className="flex flex-row justify-start items-center gap-[8px]"
            >
              <div>
                <Icon
                  type="whatsapp-color"
                  className={cn('w-[36px] h-[36px]', {
                    '!w-[32px] !h-[32px]': isMobile,
                  })}
                />
              </div>
              <div className="h-[28px]">
                <Text type="input_label" className={buttonTextStyles}>
                  Chat on WhatsApp
                </Text>
              </div>
            </Link>
          </div>

          {/* messenger icon */}
          <div className="flex flex-row justify-start items-center w-[320px] h-[48px] lg:w-[372px] lg:h-[52px] p-[8px] lg:p-[8px]">
            <Link
              prefetch={false}
              aria-label="Go to messenger chat"
              href={`${MESSENGER_BASE_URL}${usernames.facebook}`}
              passHref
              type="link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClose}
              className="flex flex-row justify-start items-center gap-[8px]"
            >
              <div>
                <Icon
                  type="messenger-color"
                  className={cn('w-[36px] h-[36px]', {
                    '!w-[32px] !h-[32px]': isMobile,
                  })}
                />
              </div>
              <div className="h-[28px]">
                <Text type="input_label" className={buttonTextStyles}>
                  Chat on Messenger
                </Text>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default ChatMenuModal;
